import { useAuth } from '@frontegg/react';
import { Progress } from 'antd';
import { Form, Formik } from 'formik';
import { debounce, isEmpty } from 'lodash';
import moment from 'moment';
import 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bars } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { analytics } from 'src/services';
import { getFromStorage, storeInLocal } from 'src/lib/storage';
import { getAppointmentUuidSlice } from 'src/store';
import {
  addRecentSearchAction,
  appointmentTypesAction,
  getAllPractitionersNames,
  getAppointmentByFilter,
  getAppointmentPractitionerAndPatients,
  getAppointmentsByStatus,
  getPatientNames,
  getRecentSearchAction,
  patientNamesOrgAction,
} from 'src/store/actions/appointment.action';
import { filteredApptsTotalSlice } from 'src/store/slices/appointments.slice';
import { showToastSuccess } from 'src/utilities/toast';
import { v4 as uuidv4 } from 'uuid';
import Calendar from '../../../components/baseComponents/Calendar';
import { Table } from '../../../components/groupComponents';
import AppointmentsMobilePage from './Appointments.MobilePage';
import AppointmentsCharts from '../../../components/pageComponents/AppointmentsCharts';
import { DeleteAppointment } from '../../../components/pageComponents/Delete.Appointment';
import { EditAppointment } from '../../../components/pageComponents/EditAppointmentPage';
import NotificationHistory from '../../../components/pageComponents/NotificationHistory';
import { ShowQRCode } from '../../../components/pageComponents/ShowQRCode.Appointment';
import { useFeatureEntitlements } from '@frontegg/react';
import AppointmentTopFilterBar from './Appointment.FilterTopBar';
import {
  getAppointmentAllEndDateFilter,
  getAppointmentAllStartDateFilter,
} from '../utils/helpers';
import { capitalizeFirstLetter } from '../utils/constants';
import { columns } from './AppointmentColumnStructure';
import { settingsOrgFlag } from '../../../store/actions/appointment.action';
import { useScreenSize } from '@insight-health-ai/roseate/utils';

// TODO: clear ununsed setstate functions in this file
const AppointmentsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { width } = useScreenSize();
  const { isEntitled: hasVCAProcessedWorkflowAccess } = useFeatureEntitlements(
    'vca-processed-status'
  );

  const { isEntitled: hideDashboardWidgetAccess } = useFeatureEntitlements(
    'hide-dashboard-widget'
  );

  const { CSVUploadEnabledForCurrentUser } = useSelector(
    (state) => state?.appointmentState
  );

  const getSortInfoFromURL = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const sortBy = searchParams.get('sortBy') || 'start_date';
    const sortDirection = searchParams.get('sortDirection') || 'asc';
    return { sortBy, sortDirection };
  }, [location.search]);

  const updateURLWithSortInfo = (sortInfo) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('sortBy', sortInfo.sortBy);
    searchParams.set('sortDirection', sortInfo.sortDirection);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const [sortInfo, setSortInfo] = useState(getSortInfoFromURL);

  useEffect(() => {
    const urlSortInfo = getSortInfoFromURL();
    if (
      urlSortInfo.sortBy !== sortInfo.sortBy ||
      urlSortInfo.sortDirection !== sortInfo.sortDirection
    ) {
      setSortInfo(urlSortInfo);
    }
  }, [getSortInfoFromURL, sortInfo.sortBy, sortInfo.sortDirection]);

  const [isLoading, setIsLoading] = useState(false);
  const [waitUntilInitialFilter, setWaitUntilInitialFilter] = useState(true);
  const isFirstLoadRef = useRef(true);
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const [selectedPractitioners, setSelectedPractitioners] = useState([]);

  const {
    getAppointmentStatusList,
    filteredValue,
    tableLoader,
    apptsTotal,
    practitionersAndPatients,
    apptsAPICall,
    filteredApptsTotal,
    remainderPatientAppts,
    recentSearchList,
  } = useSelector((state) => state?.appointmentState || {});
  const { user } = useAuth();
  const [isfilterOn, setIsFilterOn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectDates, setSelectDates] = useState([null, null]);
  const [showCurrentPractitioner, setShowCurrentPractitioner] = useState(
    getFromStorage(`${currentPractitioner?.org_uuid}_show_current_practitioner`)
  );
  const [isDefaultAppointmentView, setDefaultAppointmentView] = useState(true);

  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);

  const today = moment().format('YYYY-MM-DD');

  const showModal = () => {
    setIsModalOpen(true);
    analytics.track('Clicked Create New Appointment', { email: user?.email });
    dispatch(patientNamesOrgAction());
  };

  const handleCancel = () => {
    setIsModalOpen(!isModalOpen);
  };

  let fetchCounterRef = useRef(0);

  const [dateValue, setDateValue] = useState(
    localStorage.getItem(`${currentPractitioner?.org_uuid}_dateValue`)
  );

  const storedPractitionerNamesLocal = localStorage.getItem(
    `${currentPractitioner?.org_uuid}_storedPractitionerNames`
  );

  const parseStoredNames = useMemo(() => {
    try {
      return storedPractitionerNamesLocal
        ? JSON.parse(storedPractitionerNamesLocal)
        : [];
    } catch {
      return [];
    }
  }, [storedPractitionerNamesLocal]);

  const itemsPerPage = 50;
  const [startIndex, setStartIndex] = useState(itemsPerPage);
  const [renameTitle, setRenameTitle] = useState('');

  const isTenantMatching = user?.tenantId === settingsOrgValue?.org_uuid;

  const [filterValue, setFilterValue] = useState(null);

  const handleRecentSearchSelect = async (search) => {
    setRenameTitle('');
    try {
      setToStore(true);
      storeInLocal(
        `${currentPractitioner?.org_uuid}_show_current_practitioner`,
        false
      );
      const { practitioners, date, patient, status } = search;
      let startDate = '';
      let endDate = '';

      if (date === 'Today') {
        startDate = moment().format('YYYY-MM-DD');
        endDate = startDate;
      } else if (date === 'Yesterday') {
        startDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
        endDate = startDate;
      } else if (date === 'Tomorrow') {
        startDate = moment().add(1, 'days').format('YYYY-MM-DD');
        endDate = startDate;
      } else if (date === 'Past 7 days') {
        startDate = moment().subtract(6, 'days').format('YYYY-MM-DD');
        endDate = moment().format('YYYY-MM-DD');
      } else if (date === 'Next 7 days') {
        startDate = moment().format('YYYY-MM-DD');
        endDate = moment().add(6, 'days').format('YYYY-MM-DD');
      } else if (date === 'All time' || date === 'Custom') {
        startDate = '';
        endDate = '';
      }

      const practitionerIds = practitioners.map(
        (practitioner) => practitioner.practitionerId
      );

      const practitionerNamesArray = practitioners.flatMap((practitioner) => {
        if (typeof practitioner.practitionerName === 'string') {
          return practitioner.practitionerName
            .split(',')
            .map((name) => name.trim());
        } else {
          return practitioner.practitionerName;
        }
      });

      const doctorNameId = practitionerIds
        .flat()
        .map((innerId) => parseInt(innerId, 10));

      const filterValueUpdate = {
        ...filterValue,
        practitionerNames: practitionerNamesArray.flat(),
        doctorName: doctorNameId,
        patientName: patient,
        startDate: startDate,
        endDate: endDate,
        datefilter: date !== 'Custom' ? date : 'All time',
        status: status,
      };
      setSelectedTitle(search.title);
      localStorage.setItem(
        `${currentPractitioner?.org_uuid}_selectedTitle`,
        search.title
      );
      setMyAppointments('');
      localStorage.setItem(
        `${currentPractitioner?.org_uuid}_selectedFilters`,
        date !== 'Custom' ? date : 'All time'
      );
      setSelectDates([null, null]);
      localStorage.removeItem(`${currentPractitioner?.org_uuid}_startDate`);
      localStorage.removeItem(`${currentPractitioner?.org_uuid}_endDate`);
      // setSelectedPractitionerNamesRecent(practitionerNamesArray);
      setFilterValue(filterValueUpdate);
      setIsFilterOn(true);
      setSelectedPractitioners(doctorNameId);
      await dispatch(
        getAppointmentByFilter(
          doctorNameId,
          patient,
          getAppointmentAllStartDateFilter(startDate),
          getAppointmentAllEndDateFilter(endDate),
          status,
          'normal',
          startIndex,
          '',
          sortInfo?.sortBy,
          sortInfo?.sortDirection
        )
      );
    } catch (error) {
      console.error('Error handling recent search:', error);
    }
  };

  const initialfilterValue = useMemo(() => {
    // Wait for API response before initializing (null = not loaded yet)
    if (!recentSearchList) {
      return null;
    }

    const defaultAppointmentView = recentSearchList?.search_preference?.find(
      (record) => record.isDefaultAppointmentView === true
    );

    if (defaultAppointmentView) {
      return defaultAppointmentView;
    }

    const appointmentView = localStorage.getItem('appointment_view');
    if (appointmentView === 'CALENDAR') {
      return { ...filterValue };
    }

    const reloadFilterValue = JSON.parse(
      localStorage.getItem('filterValueOnreload')
    );

    const startDate = reloadFilterValue?.startDate || today;
    const endDate = reloadFilterValue?.endDate || today;
    const practitioner_ids =
      reloadFilterValue?.doctorName?.length > 0
        ? reloadFilterValue?.doctorName
        : settingsOrgValue?.has_appointments &&
            showCurrentPractitioner !== 'false'
          ? [currentPractitioner?.practitioner_id]
          : [];

    const patient_name = reloadFilterValue?.patientName || [];
    const patient_id = reloadFilterValue?.patientId || '';
    setSelectedPractitioners(practitioner_ids);

    if (!isEmpty(reloadFilterValue)) {
      setIsFilterOn(true);
      if (reloadFilterValue.datefilter === 'All time') {
        if (
          reloadFilterValue?.doctorName?.length === 0 &&
          reloadFilterValue?.patientName === '' &&
          (reloadFilterValue?.status === '' ||
            reloadFilterValue?.status.toLowerCase() === 'completed')
        ) {
          setIsFilterOn(false);
          dispatch(
            getAppointmentsByStatus(
              startIndex,
              undefined,
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          );
        } else {
          setIsFilterOn(true);
          dispatch(
            getAppointmentByFilter(
              practitioner_ids,
              patient_name,
              getAppointmentAllStartDateFilter(''),
              getAppointmentAllEndDateFilter(''),
              reloadFilterValue ? reloadFilterValue.status : '',
              'normal',
              startIndex,
              patient_id,
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          );
        }
      } else {
        dispatch(
          getAppointmentByFilter(
            practitioner_ids,
            patient_name,
            getAppointmentAllStartDateFilter(startDate),
            getAppointmentAllEndDateFilter(endDate),
            reloadFilterValue ? reloadFilterValue.status : '',
            'normal',
            startIndex,
            patient_id,
            sortInfo.sortBy,
            sortInfo.sortDirection
          )
        );
      }
    } else {
      dispatch(
        getAppointmentByFilter(
          practitioner_ids,
          patient_name,
          getAppointmentAllStartDateFilter(startDate),
          getAppointmentAllEndDateFilter(endDate),
          reloadFilterValue ? reloadFilterValue.status : '',
          'normal',
          startIndex,
          patient_id,
          sortInfo.sortBy,
          sortInfo.sortDirection
        )
      );
    }

    return {
      practitionerNames: reloadFilterValue
        ? reloadFilterValue.practitionerNames
        : [], //check
      doctorName: practitioner_ids,
      patientName: reloadFilterValue ? reloadFilterValue.patientName : '',
      patientId: reloadFilterValue ? reloadFilterValue.patientId : '',
      datefilter: reloadFilterValue ? reloadFilterValue.datefilter : 'Today',
      status: reloadFilterValue ? reloadFilterValue.status : '',
      startDate: reloadFilterValue ? reloadFilterValue.startDate : startDate,
      endDate: reloadFilterValue ? reloadFilterValue.endDate : endDate,
    };
  }, [dispatch, startIndex, recentSearchList]);

  useEffect(() => {
    if (!initialfilterValue || !isFirstLoadRef.current) {
      return;
    }

    if (
      typeof initialfilterValue === 'object' &&
      initialfilterValue?.isDefaultAppointmentView
    ) {
      handleRecentSearchSelect(initialfilterValue);
      isFirstLoadRef.current = false;
    } else {
      setFilterValue(initialfilterValue);
      isFirstLoadRef.current = false;
    }
  }, [initialfilterValue]);

  const [showProfileLogo, setShowProfileLogo] = useState(true);
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    doctorNames: [],
    patientNames: [],
  });
  const [filteredPatientNames, setFilteredPatientNames] = useState([]);
  const [showNoData, setShowNoData] = useState(false);

  const [patientSearchKeyword, setPatientSearchKeyword] = useState('');
  const [practitionerSearchKeyword, setpractitionerSearchKeyword] =
    useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isQRCodeOpen, setIsQRCodeOpen] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showUploadHistory, setShowUploadHistory] = useState(false);
  const [, setUploadKey] = useState(0);
  const [isCustomSelected, setIsCustomSelected] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [
    showConvertAppointmentToAuraModal,
    setShowConvertAppointmentToAuraModal,
  ] = useState(false);
  const [
    selectedAppointmentForAuraEncounter,
    setSelectedAppointmentForAuraEncounter,
  ] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isMangeViewOpen, setIsMangeViewOpen] = useState(false);
  const [myAppointments, setMyAppointments] = useState(
    () =>
      localStorage.getItem(`${currentPractitioner?.org_uuid}_myAppointments`) ||
      ''
  );
  const [selectedTitle, setSelectedTitle] = useState(() => {
    if (!currentPractitioner?.org_uuid) {
      return '';
    }
    return (
      localStorage.getItem(`${currentPractitioner.org_uuid}_selectedTitle`) ||
      ''
    );
  });

  const [selectedFilters, setSelectedFilters] = useState(
    localStorage.getItem(`${currentPractitioner?.org_uuid}_selectedFilters`)
  );
  const [resetDateFilter, setResetDateFilter] = useState(false);
  const [defaultAppointmentType, setDefaultAppointmentType] = useState();

  useEffect(() => {
    const show_current_practitioner = getFromStorage(
      `${currentPractitioner?.org_uuid}_show_current_practitioner`
    );
    setShowCurrentPractitioner(show_current_practitioner);
  }, [currentPractitioner?.org_uuid, filterValue]);

  useEffect(() => {
    if (waitUntilInitialFilter) {
      setFilterValue(initialfilterValue);
      setWaitUntilInitialFilter(false);
    }
  }, [initialfilterValue, waitUntilInitialFilter]);

  const [deleteText, setDeleteText] = useState('');
  const [QRCodeURL, setQRCodeURL] = useState('');
  const [editAppointmentOpen, setEditAppointmentOpen] = useState(false);
  const [, setVisibleItems] = useState([]);
  const [toStore, setToStore] = useState(false);
  const [isnotificationHistoryOpen, setIsnotificationHistoryOpen] =
    useState(false);
  const [storeRecord, setStoreRecord] = useState({});
  const [duplicateError, setDuplicateError] = useState(false);
  const hideDateTime = window.innerWidth <= 1366;
  const nonVCACondition = 'Aura only';
  const [startDate, setStartDate] = useState();
  // localStorage.getItem(`${currentPractitioner?.org_uuid}_startDate`)
  const [endDate, setEndDate] = useState();
  // localStorage.getItem(`${currentPractitioner?.org_uuid}_endDate`)
  const [appointmentsView, setAppointmentsView] = useState('LIST');

  const [, setRecentSearches] = useState([]);
  const addRecentSearch = (
    practitionerId,
    practitionerName,
    patient,
    date,
    status,
    title,
    isDefaultAppointmentView
  ) => {
    const newSearch = {
      practitioners: [
        {
          practitionerId: practitionerId || [],
          practitionerName: practitionerName || '',
        },
      ],
      status: status || '',
      title: title || '',
      patient: patient || '',
      date: date || '',
      isDefaultAppointmentView: isDefaultAppointmentView || false,
      id: uuidv4(),

      last_modified_by: currentPractitioner
        ? currentPractitioner.practitioner_name
        : '',
      created_by: currentPractitioner
        ? currentPractitioner.practitioner_name
        : '',
    };
    if (recentSearchList?.search_preference?.length > 0) {
      let updatedSearches = recentSearchList?.search_preference || [];

      // If the new search has isDefaultAppointmentView = true, set all existing ones with isDefaultAppointmentView = true to false
      if (isDefaultAppointmentView) {
        updatedSearches = updatedSearches.map((search) => {
          if (search.isDefaultAppointmentView) {
            return { ...search, isDefaultAppointmentView: false };
          }
          return search;
        });
      }

      updatedSearches = [newSearch, ...updatedSearches];
      dispatch(addRecentSearchAction(updatedSearches, 'appointments'));
      showToastSuccess('View Saved');
    } else {
      setRecentSearches([newSearch]);
      dispatch(addRecentSearchAction([newSearch], 'appointments'));
      showToastSuccess('View Saved');
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const perPageSize = queryParams.get('per_page');
    if (perPageSize) {
      const pageSizeToSet = apptsTotal
        ? Math.min(Number(perPageSize), apptsTotal)
        : Number(perPageSize);
      setStartIndex(pageSizeToSet);
      setVisibleItems((prevItems) => [
        ...prevItems,
        ...filteredData.slice(startIndex, perPageSize),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleAddLabel = () => {
    analytics.track('Clicked Save This View In Recent Searches', {
      email: user?.email,
    });
    setIsModalVisible(true);
  };

  const handleMangeViews = () => {
    analytics.track('Clicked Manage Views In Recent Searches', {
      email: user?.email,
    });
    setIsMangeViewOpen(true);
  };

  const handleCloseManageView = () => {
    analytics.track('Clicked Cancel Button In Manage Views Modal', {
      email: user?.email,
    });
    setIsMangeViewOpen(false);
  };
  const handleSaveView = () => {
    analytics.track('Clicked Submit After Adding View In Recent Searches', {
      title: renameTitle,
      email: user?.email,
    });
    addRecentSearch(
      filterValue?.doctorName,
      parseStoredNames,
      filterValue?.patientName,
      filterValue?.datefilter,
      filterValue?.status,
      renameTitle,
      isDefaultAppointmentView
    );
    setRenameTitle('');
    setIsModalVisible(false);
    storeInLocal(`${currentPractitioner?.org_uuid}_selectedTitle`, renameTitle);
    setDuplicateError(false);
  };

  const handleSaveAppDuplicate = () => {
    const existingTitles =
      recentSearchList?.search_preference?.map((search) => search.title) || [];
    const isDuplicate = existingTitles.includes(renameTitle.trim());
    if (isDuplicate) {
      setDuplicateError(true);
      return;
    }
    handleSaveView();
  };

  const handleCloseView = () => {
    setDuplicateError(false);
    analytics.track('Clicked Cancel In Save This View Modal', {
      email: user?.email,
    });
    setIsModalVisible(false);
    setRenameTitle('');
  };

  const handleShowMore = () => {
    setIsLoading(true);
    const endIndex = startIndex + itemsPerPage;

    const url = new URL(window.location);
    url.searchParams.set('per_page', endIndex);
    window.history.pushState({}, '', url);

    setStartIndex(endIndex);
    setVisibleItems((prevItems) => [
      ...prevItems,
      ...filteredData.slice(startIndex, endIndex),
    ]);
  };

  useEffect(() => {
    if (filteredData) {
      const currentItems = filteredData.slice(
        itemsPerPage,
        startIndex + itemsPerPage
      );
      setVisibleItems(currentItems);
    }
  }, [filteredData, startIndex, itemsPerPage]);

  useEffect(() => {
    dispatch(settingsOrgFlag());
  }, [dispatch]);

  const handleCurrentPractitionerNext7Days = async () => {
    try {
      setShowProfileLogo(false);
      const startDate = moment().format('YYYY-MM-DD');
      const endDate = moment().add(6, 'days').format('YYYY-MM-DD');
      const doctorNameValue = currentPractitioner.practitioner_id
        ? currentPractitioner.practitioner_id
        : currentPractitioner.practitioner_name;
      const next7DaysValue = 'Next 7 days';
      setDateValue(next7DaysValue);

      if (currentPractitioner?.org_uuid) {
        localStorage.setItem(
          `${currentPractitioner?.org_uuid}_next7days`,
          next7DaysValue
        );
        localStorage.setItem(
          `${currentPractitioner?.org_uuid}_selectedFilters`,
          'Next 7 days'
        );
      }
      // Clear the start date and end date fields
      localStorage.removeItem(`${currentPractitioner?.org_uuid}_startDate`);
      localStorage.removeItem(`${currentPractitioner?.org_uuid}_endDate`);

      const updatedFilterValue = {
        ...filterValue,
        doctorName: [doctorNameValue],
        patientName: '',
        startDate: startDate,
        endDate: endDate,
        datefilter: 'next7days',
        status: '',
      };
      setSelectedPractitioners([doctorNameValue]);
      setFilterValue(updatedFilterValue);
      // Trigger the API call
      dispatch(
        getAppointmentByFilter(
          [currentPractitioner.practitioner_id],
          '',
          getAppointmentAllStartDateFilter(startDate),
          getAppointmentAllEndDateFilter(endDate),
          '',
          'normal',
          startIndex,
          '',
          sortInfo.sortBy,
          sortInfo.sortDirection
        )
      );
      setSelectDates(null, null);
      setIsFilterOn(true);
    } catch (error) {
      console.error('Error handling current practitioner appointments:', error);
    }
  };

  const [showDropdown, setShowDropdown] = useState(false);

  const debouncedFetchPatientNames = useCallback(
    debounce(async (value) => {
      await dispatch(getPatientNames(value));
      setShowDropdown(true);
    }, 250),
    []
  );

  const onChangeHandler = async (value, fieldName, dates, patientId) => {
    storeInLocal(
      `${currentPractitioner?.org_uuid}_show_current_practitioner`,
      false
    );
    // Since custom filters are applied, we need to reset the name of default custom view
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_selectedTitle`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_myAppointments`);
    setSelectedTitle('');
    setMyAppointments('');
    setRenameTitle('');

    setToStore(true);
    if (fieldName === 'datefilter') {
      analytics.track('Entered Date Filter', {
        date_filter: value,
        email: user?.email,
      });
      let startDate = '';
      let endDate = '';
      if (value === 'Custom…🗓️') {
        handleCustomSelect();
      } else {
        setIsCustomSelected(false);

        if (value === 'Today') {
          startDate = moment().format('YYYY-MM-DD');
          endDate = startDate;
        } else if (value === 'Yesterday') {
          startDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
          endDate = startDate;
        } else if (value === 'Tomorrow') {
          startDate = moment().add(1, 'days').format('YYYY-MM-DD');
          endDate = startDate;
        } else if (value === 'Past 7 days') {
          startDate = moment().subtract(6, 'days').format('YYYY-MM-DD');
          endDate = moment().format('YYYY-MM-DD');
        } else if (value === 'Next 7 days') {
          startDate = moment().format('YYYY-MM-DD');
          endDate = moment().add(6, 'days').format('YYYY-MM-DD');
        } else if (value === 'All time') {
          startDate = getAppointmentAllStartDateFilter('');
          endDate = getAppointmentAllEndDateFilter('');
        }
        setFilterValue({
          ...filterValue,
          startDate: startDate,
          endDate: endDate,
          datefilter: value,
        });
        if (
          (startDate && endDate) ||
          filterValue?.doctorName?.length > 0 ||
          filterValue?.patientName ||
          filterValue?.status
        ) {
          dispatch(
            getAppointmentByFilter(
              filterValue?.doctorName?.[0] === ''
                ? []
                : filterValue?.doctorName,
              filterValue?.patientName === null ? '' : filterValue.patientName,
              getAppointmentAllStartDateFilter(startDate),
              getAppointmentAllEndDateFilter(endDate),
              filterValue?.status === null ? '' : filterValue?.status,
              'normal',
              startIndex,
              filterValue?.patientId || '',
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          ) && setIsFilterOn(true);
        } else if (value === 'All time') {
          if (
            filterValue?.doctorName?.length === 0 &&
            filterValue?.patientName === '' &&
            filterValue?.status === ''
          ) {
            setIsFilterOn(false);
            await dispatch(
              getAppointmentsByStatus(
                startIndex,
                undefined,
                sortInfo.sortBy,
                sortInfo.sortDirection
              )
            );
          } else {
            dispatch(
              getAppointmentByFilter(
                filterValue?.doctorName?.[0] === ''
                  ? []
                  : filterValue?.doctorName,
                filterValue?.patientName === null
                  ? ''
                  : filterValue?.patientName,
                getAppointmentAllStartDateFilter(''),
                getAppointmentAllEndDateFilter(''),
                filterValue?.status === null ? '' : filterValue?.status,
                'normal',
                startIndex,
                filterValue?.patientId || '',
                sortInfo.sortBy,
                sortInfo.sortDirection
              )
            ) && setIsFilterOn(true);
          }
        }
      }
      setFilterValue({
        ...filterValue,
        ['datefilter']: value,
        startDate: startDate,
        endDate: endDate,
      });
    } else if (fieldName === 'doctorName') {
      let globalDoctorIds = [];
      let startDate = '';
      let endDate = '';

      startDate = filterValue?.startDate;
      endDate = filterValue?.endDate;

      value.forEach((val) => {
        if (isNaN(val)) {
          const doctor = filterOptions?.doctorNames?.find(
            (doc) => doc.value.trim() === val.trim()
          );
          if (doctor) {
            globalDoctorIds.push(doctor.id);
          }
        } else {
          globalDoctorIds.push(val);
        }
      });

      const patientName = filterOptions?.patientNames?.filter(
        (data) => data.value === filterValue?.patientName
      )?.[0]?.id;
      setFilterValue({
        ...filterValue,
        doctorName: value !== 'All' ? globalDoctorIds : [],
        practitionerNames: parseStoredNames,
      });
      if (
        value?.length === 0 &&
        !filterValue?.patientName &&
        !filterValue?.startDate &&
        !filterValue?.endDate &&
        !filterValue?.status
      ) {
        await dispatch(
          getAppointmentsByStatus(
            startIndex,
            undefined,
            sortInfo.sortBy,
            sortInfo.sortDirection
          )
        );
        setIsFilterOn(false);
        fetchCounterRef.current = 0;
      } else if (value?.length === 0) {
        dispatch(
          getAppointmentByFilter(
            [],
            patientName,
            getAppointmentAllStartDateFilter(startDate),
            getAppointmentAllEndDateFilter(endDate),
            filterValue?.status === null ? '' : filterValue?.status,
            'normal',
            startIndex,
            filterValue?.patientId || '',
            sortInfo.sortBy,
            sortInfo.sortDirection
          )
        ) && setIsFilterOn(true);
      } else {
        value &&
          dispatch(
            getAppointmentByFilter(
              globalDoctorIds,
              patientName,
              getAppointmentAllStartDateFilter(startDate),
              getAppointmentAllEndDateFilter(endDate),
              filterValue?.status === null ? '' : filterValue?.status,
              'normal',
              startIndex,
              filterValue?.patientId || '',
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          ) &&
          setIsFilterOn(true);
      }
      const selectedPractitionerNames = value
        .map((id) => {
          const practitioner = filterOptions?.doctorNames?.find(
            (doc) => doc.id === id
          );
          return practitioner ? practitioner.value : '';
        })
        .filter((name) => name !== '');

      const updatedSelectedPractitionerNames = [...selectedPractitionerNames];
      setFilterValue({
        ...filterValue,
        practitionerNames: updatedSelectedPractitionerNames,
        doctorName: globalDoctorIds,
      });

      localStorage.setItem(
        `${currentPractitioner?.org_uuid}_storedPractitionerNames`,
        JSON.stringify(updatedSelectedPractitionerNames)
      );

      // setSelectedPractitionerNamesLocal(updatedSelectedPractitionerNames);
    } else if (fieldName === 'patientName') {
      analytics.track('Entered Patient Name Search', { email: user?.email });
      setFilterValue({
        ...filterValue,
        ['patientName']: value,
        patientId: patientId || '',
      });
      let startDate = '';
      let endDate = '';

      startDate = filterValue?.startDate;
      endDate = filterValue?.endDate;

      if (value === '') {
        setFilteredPatientNames([]);
        const patientName = '';
        if (
          (filterValue?.doctorName?.length === 0 ||
            filterValue?.doctorName?.[0] === '') &&
          !patientName &&
          !startDate &&
          !endDate &&
          !filterValue?.status
        ) {
          fetchCounterRef.current = 0;
          setIsFilterOn(false);
          setFilterValue({
            ...filterValue,
            patientName: '',
            patientId: '',
          });
          await dispatch(
            getAppointmentsByStatus(
              startIndex,
              undefined,
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          );
        } else {
          dispatch(
            getAppointmentByFilter(
              filterValue?.doctorName?.[0] === ''
                ? []
                : filterValue?.doctorName,
              patientName,
              getAppointmentAllStartDateFilter(startDate),
              getAppointmentAllEndDateFilter(endDate),
              filterValue?.status === null ? '' : filterValue?.status,
              'normal',
              startIndex,
              patientId || '',
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          ) && setIsFilterOn(true);
        }
      } else {
        const patientName = filterOptions?.patientNames?.filter(
          (data) => data.value === value
        )?.[0]?.id;
        if (patientName) {
          dispatch(
            getAppointmentByFilter(
              filterValue?.doctorName?.[0] === ''
                ? []
                : filterValue?.doctorName,
              patientName,
              getAppointmentAllStartDateFilter(startDate),
              getAppointmentAllEndDateFilter(endDate),
              filterValue?.status === null ? '' : filterValue?.status,
              'normal',
              startIndex,
              patientId || '',
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          ) && setIsFilterOn(true);
        }
      }
    } else if (fieldName === 'dateRange') {
      const [start, end] = dates;
      const startDate = start ? moment(start).format('YYYY-MM-DD') : '';
      const endDate = end ? moment(end).format('YYYY-MM-DD') : '';

      setFilterValue({
        ...filterValue,
        startDate: startDate,
        endDate: endDate,
      });

      const patientName = filterOptions?.patientNames?.filter(
        (data) => data.value === filterValue.patientName
      )?.[0]?.id;

      if (
        (startDate && endDate) ||
        filterValue?.doctorName?.length > 0 ||
        patientName ||
        filterValue?.status
      ) {
        analytics.track('Applied Date Range Filter', {
          start_date: start,
          end_date: end,
          email: user?.email,
        });
        dispatch(
          getAppointmentByFilter(
            filterValue?.doctorName?.[0] === '' ? [] : filterValue?.doctorName,
            patientName,
            getAppointmentAllStartDateFilter(startDate),
            getAppointmentAllEndDateFilter(endDate),
            filterValue?.status === null ? '' : filterValue?.status,
            'normal',
            startIndex,
            filterValue?.patientId || '',
            sortInfo.sortBy,
            sortInfo.sortDirection
          )
        );
        setIsFilterOn(true);
      } else {
        fetchCounterRef.current = 0;
        setIsFilterOn(false);
      }
    } else if (fieldName === 'status') {
      analytics.track('Selected Status Filter', {
        status: value,
        email: user?.email,
      });
      let startDate = '';
      let endDate = '';
      startDate = filterValue?.startDate;
      endDate = filterValue?.endDate;

      if (value === 'intake-All' || value === 'invisit-All') {
        setFilterValue({
          ...filterValue,
          status: '',
        });

        if (
          (filterValue?.doctorName?.length === 0 ||
            filterValue?.doctorName?.[0] === '') &&
          !filterValue?.patientName &&
          !filterValue?.startDate &&
          !filterValue?.endDate
        ) {
          await dispatch(
            getAppointmentsByStatus(
              startIndex,
              undefined,
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          );
          setIsFilterOn(false);
          fetchCounterRef.current = 0;
        } else {
          setIsFilterOn(true);
          dispatch(
            getAppointmentByFilter(
              filterValue?.doctorName?.[0] === ''
                ? []
                : filterValue?.doctorName,
              filterValue.patientName === null ? '' : filterValue.patientName,
              getAppointmentAllStartDateFilter(startDate),
              getAppointmentAllEndDateFilter(endDate),
              '',
              'normal',
              startIndex,
              filterValue?.patientId || '',
              sortInfo.sortBy,
              sortInfo.sortDirection
            )
          );
        }
      } else {
        setFilterValue({
          ...filterValue,
          status: value,
        });
        dispatch(
          getAppointmentByFilter(
            filterValue?.doctorName?.[0] === '' ? [] : filterValue?.doctorName,
            filterValue.patientName === null ? '' : filterValue.patientName,
            getAppointmentAllStartDateFilter(startDate),
            getAppointmentAllEndDateFilter(endDate),
            value,
            'normal',
            startIndex,
            filterValue?.patientId || '',
            sortInfo.sortBy,
            sortInfo.sortDirection
          )
        ) && setIsFilterOn(true);
      }
    }
  };
  useEffect(() => {
    if (toStore) {
      localStorage.setItem('filterValueOnreload', JSON.stringify(filterValue));
    }
  }, [filterValue]);

  useEffect(() => {
    dispatch(getAppointmentUuidSlice({}));
  }, [dispatch]);

  const handleCustomSelect = () => {
    setIsCustomSelected(true);
    setModalVisible(true);
  };

  const handleCancelModal = () => {
    setIsCustomSelected(false);
    setModalVisible(false);
  };

  const onCellClick = (record) => {
    return {
      onClick: () => {
        handleRowClick(record, 'Cell');
      },
    };
  };

  const hideConvertAppointmentToAuraModal = () => {
    setShowConvertAppointmentToAuraModal(false);
    setSelectedAppointmentForAuraEncounter(null);
  };

  const handleRowClick = (record, type) => {
    storeInLocal('uuid', record?.appt_uuid);
    analytics.track('Opened CareConnect Summary', {
      appointment_date: record?.appointmentDateTime,
      appointment_id: record?.appointment_id,
      appointment_status: record?.intakeStatus,
      practitioner_name: record?.practitioner_Name,
      email: user?.email,
    });
    if (type === 'Menu') {
      analytics.track('Clicked View Summary Action Menu', {
        appointment_date: record?.appointmentDateTime,
        appointment_id: record?.appointment_id,
        appointment_status: record?.intakeStatus,
        practitioner_name: record?.practitioner_Name,
        email: user?.email,
      });
    }
    navigate(`/schedule/${record?.appt_uuid}`);
  };

  useEffect(() => {
    if (
      getAppointmentStatusList &&
      Array.isArray(getAppointmentStatusList) &&
      getAppointmentStatusList.length > 0
    ) {
      let doctorNames = [
          {
            label: 'All',
            value: 'All',
          },
        ],
        patientNames = [];
      let newData = null;
      newData = getAppointmentStatusList?.map((list, index) => {
        const practitionerName =
          list?.practitioner_firstName || list?.practitioner_lastName
            ? `${list.practitioner_firstName} ${list.practitioner_lastName}`
            : 'N/A';

        const patientName =
          list?.patient_firstName || list?.patient_lastName
            ? `${list.patient_firstName} ${list.patient_lastName}`
            : 'N/A';

        !doctorNames.some(
          (doctor) =>
            doctor.value ===
            `${list?.practitioner_title} ${practitionerName.trim()}`
        ) &&
          doctorNames.push({
            id: list?.practitioner_id,
            label: `${
              list?.practitioner_title ? list?.practitioner_title + '. ' : ''
            }${practitionerName.trim()}`,
            value: `${
              list?.practitioner_title ? list?.practitioner_title + '. ' : ''
            }${practitionerName.trim()}`,
          });
        !patientNames.some((patient) => patient.value === patientName.trim()) &&
          patientNames.push({
            id: patientName.trim(),
            value: patientName.trim(),
            patient_id: list?.patient_id,
          });
        return {
          key: index,
          practitioner_Name: `${
            list?.practitioner_title ? list?.practitioner_title + '. ' : ''
          } ${practitionerName?.trim()}`,

          patient_Name: patientName.trim(),
          appointmentDateTime: {
            date: list?.start_date ? list?.start_date : 'N/A',
            time: list?.start_time ? list?.start_time : 'N/A',
          },
          practitioner_firstName: list?.practitioner_firstName,
          practitioner_lastName: list?.practitioner_lastName,
          dateofBirth: list?.birth_date ? list?.birth_date : 'N/A',
          patient_lastName: list?.patient_lastName,
          appointment_id: list?.appointment_id,
          intakeStatus: list?.chat_completion_status,
          isProcessed: list?.is_processed,
          llm_chat_url: list?.llm_chat_url,
          appt_uuid: list?.appt_uuid,
          settings: list?.settings,
          patient_id: list?.patient_id,
          patient_ehr_id: list?.patient_ehr_id,
          condition_name: list?.condition_name
            ? list?.condition_name == 'Unknown'
              ? nonVCACondition
              : list?.condition_name
            : 'N/A',
          notification_status: list?.notification_status,
          isExpired: list?.isExpired,
          isOptOut: list?.is_opted_out,
          encounterInVisitStatus: list?.encounter_in_visit_status,
          encounterPreChartStatus: list?.encounter_pre_chart_status,
          actions:
            list?.settings?.enable_patient_email_notifications ||
            list?.settings?.enable_patient_sms_notifications,
        };
      });
      if (newData && Array.isArray(newData) && newData.length > 0) {
        setData(newData);
        setFilteredData(newData);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [getAppointmentStatusList]);

  useEffect(() => {
    const DoctorArray = practitionersAndPatients?.practitioner?.map(
      (practitioner, index, array) => ({
        id: practitioner?.practitioner_id,
        value:
          array.filter(
            (p) =>
              p.practitioner_firstName === practitioner.practitioner_firstName
          ).length > 1
            ? `${practitioner.practitioner_firstName}`
            : practitioner.practitioner_firstName,
        key: practitioner?.practitioner_id,
      })
    );

    DoctorArray?.unshift({ label: 'All', value: 'All' });

    const uniquePatientNames = new Set();
    const PatientArray = practitionersAndPatients?.patient?.reduce(
      (accumulator, patient) => {
        const patientName = `${patient?.patient_Name} (${patient?.patient_id})`;
        if (!uniquePatientNames.has(patientName)) {
          uniquePatientNames.add(patientName);
          accumulator.push({
            id: patient?.patient_Name,
            value: patient?.patient_Name,
            patient_id: patient?.patient_id,
            patient_dob: patient?.patient_birth_date
              ? moment(patient.patient_birth_date).format('MM/DD/YYYY')
              : null,
            appointment_date: patient?.appointment_date
              ? moment(patient.appointment_date).format('MM/DD/YYYY')
              : null,
            ehr_id: patient?.ehr_id,
          });
        }
        return accumulator;
      },
      []
    );

    setFilterOptions({
      doctorNames: DoctorArray,
      patientNames: PatientArray,
    });
  }, [practitionersAndPatients]);

  useEffect(() => {
    if (
      filteredValue &&
      Array.isArray(filteredValue) &&
      filteredValue?.length > 0
    ) {
      let newData = filteredValue?.map((list, index) => {
        const practitionerName =
          list?.practitioner_firstName || list?.practitioner_lastName
            ? `${list.practitioner_firstName} ${list.practitioner_lastName}`
            : 'N/A';
        const patientName =
          list?.patient_firstName || list?.patient_lastName
            ? `${list.patient_firstName} ${list.patient_lastName}`
            : 'N/A';
        return {
          key: index,
          practitioner_Name: `${
            list?.practitioner_title ? list?.practitioner_title + '. ' : ''
          }${practitionerName.trim()}`,
          patient_Name: patientName.trim(),
          appointmentDateTime: {
            date: list?.start_date ? list?.start_date : 'N/A',
            time: list?.start_time ? list?.start_time : 'N/A',
          },
          dateofBirth: list?.birth_date ? list?.birth_date : 'N/A',
          patient_lastName: list?.patient_lastName,
          practitioner_firstName: list?.practitioner_firstName,
          practitioner_lastName: list?.practitioner_lastName,
          appointment_id: list?.appointment_id,
          intakeStatus: list?.chat_completion_status,
          isProcessed: list?.is_processed,
          llm_chat_url: list?.llm_chat_url,
          appt_uuid: list?.appt_uuid,
          condition_name: list?.condition_name
            ? list?.condition_name == 'Unknown'
              ? nonVCACondition
              : list?.condition_name
            : 'N/A',
          notification_status: list?.notification_status,
          isExpired: list?.isExpired,
          isOptOut: list?.is_opted_out,
          patient_id: list?.patient_id,
          patient_ehr_id: list?.patient_ehr_id,
          encounterInVisitStatus: list?.encounter_in_visit_status,
          encounterPreChartStatus: list?.encounter_pre_chart_status,
          actions:
            list?.settings?.enable_patient_email_notifications ||
            list?.settings?.enable_patient_sms_notifications,
        };
      });
      if (newData && Array.isArray(newData) && newData.length > 0) {
        setFilteredData(newData);
        setIsLoading(false);
      }
    } else {
      setFilteredData([]);
      setIsLoading(false);
    }
  }, [filteredValue]);

  useEffect(() => {
    const payload = {
      is_new_appointment: true,
    };
    dispatch(appointmentTypesAction());
    dispatch(getRecentSearchAction('appointments'));
    dispatch(getAllPractitionersNames(payload));
    dispatch(getAppointmentPractitionerAndPatients());
  }, [dispatch]);

  useEffect(() => {
    if (appointmentsView === 'LIST') {
      localStorage.setItem('appointment_view', 'LIST');
    }
  }, []);

  useEffect(() => {
    analytics.page();
    analytics.track('Viewed Appointments List', { email: user?.email });
  }, []);
  useEffect(() => {
    setIsLoading(tableLoader);
  }, [tableLoader]);

  const denominator =
    filteredApptsTotal != null ? filteredApptsTotal : apptsTotal;
  const beforepercent = filteredData?.length / denominator;

  function hasMultiplePractitionersWithSameName(firstName) {
    const practitionersWithSameName =
      practitionersAndPatients?.practitioner.filter(
        (practitioner) => practitioner.practitioner_firstName === firstName
      );
    return practitionersWithSameName.length > 1;
  }
  const performFuzzySearch = (value) => {
    const search = value.toLowerCase().trim();
    if (!filterOptions || !filterOptions.patientNames) {
      return [];
    }

    return filterOptions.patientNames.filter((patient) => {
      return (
        patient.value.toLowerCase().includes(search) ||
        String(patient.patient_id).toLowerCase().includes(search) ||
        (patient.ehr_id &&
          String(patient.ehr_id).toLowerCase().includes(search)) ||
        (patient.patient_dob &&
          patient.patient_dob.toLowerCase().includes(search))
      );
    });
  };

  const isMobile = window.innerWidth < 1260;

  const [currentDate, setCurrentDate] = useState(new Date());

  const handleMonthChange = (increment) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setCurrentDate(newDate);
  };

  const handleYearChange = (increment) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() + increment);
    setCurrentDate(newDate);
  };

  // Custom sort handler for header clicks
  const handleSort = (sortBy, sortDirection) => {
    const newSortInfo = {
      sortBy,
      sortDirection,
    };
    setSortInfo(newSortInfo);
    updateURLWithSortInfo(newSortInfo);

    // Trigger API call with new sorting
    if (
      filterValue?.doctorName?.length === 0 &&
      filterValue?.patientName === '' &&
      filterValue?.status === '' &&
      filterValue?.datefilter === 'All time'
    ) {
      dispatch(
        getAppointmentsByStatus(startIndex, undefined, sortBy, sortDirection)
      );
    } else {
      dispatch(
        getAppointmentByFilter(
          filterValue?.doctorName || [],
          filterValue?.patientName || '',
          getAppointmentAllStartDateFilter(filterValue?.startDate || ''),
          getAppointmentAllEndDateFilter(filterValue?.endDate || ''),
          filterValue?.status || '',
          'normal',
          startIndex,
          filterValue?.patientId || '',
          sortBy,
          sortDirection
        )
      );
    }
  };

  const handleReset = async (type) => {
    // Common actions for both reset types ie. patient reset and normal reset
    localStorage.removeItem(
      `${currentPractitioner?.org_uuid}_selectedPractitioners`
    );
    localStorage.removeItem(
      `${currentPractitioner?.org_uuid}_storedPractitionerNames`
    );
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_dateValue`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_filter_status`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_selectedTitle`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_myAppointments`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_selectDates`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_startDate`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_endDate`);
    storeInLocal(
      `${currentPractitioner?.org_uuid}_show_current_practitioner`,
      false
    );
    setSelectDates([null, null]);
    dispatch(filteredApptsTotalSlice(null));
    setShowProfileLogo(true);
    setSelectedPractitioners([]);
    setRenameTitle('');
    setMyAppointments('');
    setSelectedTitle('');
    setIsFilterOn(true);
    // setSelectedPractitionerNamesLocal([]);
    // setSelectedPractitionerNamesRecent([]);
    setpractitionerSearchKeyword(null);

    const defaultAppointmentView = recentSearchList?.search_preference?.find(
      (record) => record.isDefaultAppointmentView === true
    );

    if (defaultAppointmentView && type !== 'patientReset') {
      // Use the default view from recentSearchList
      await handleRecentSearchSelect(defaultAppointmentView);
    } else {
      // Fall back to original reset behavior
      if (type === 'patientReset') {
        localStorage.setItem(
          `${currentPractitioner?.org_uuid}_selectedFilters`,
          'All time'
        );
        setSelectedFilters('All time');
        setDateValue('All Time');
      } else if (type === 'reset') {
        setResetDateFilter(true);

        localStorage.removeItem(
          `${currentPractitioner?.org_uuid}_filter_patient_name`
        );
        localStorage.setItem(
          `${currentPractitioner?.org_uuid}_selectedFilters`,
          'Today'
        );
        setDateValue('Today');
        setPatientSearchKeyword('');
        setFilteredPatientNames([]);
      }

      const startDate = type === 'reset' ? moment().format('YYYY-MM-DD') : '';
      const endDate = type === 'reset' ? moment().format('YYYY-MM-DD') : '';
      const datefilter = type === 'reset' ? 'Today' : 'All time';
      const patientName =
        type === 'patientReset' ? filterValue?.patientName : '';
      const patientId = type === 'patientReset' ? filterValue?.patientId : '';

      setToStore(true);
      setFilterValue({
        practitionerNames: [],
        doctorName: [],
        patientName,
        startDate,
        endDate,
        datefilter,
        status: '',
        patientId,
      });

      await dispatch(
        getAppointmentByFilter(
          [],
          patientName,
          getAppointmentAllStartDateFilter(startDate) || 'All Time',
          getAppointmentAllEndDateFilter(endDate) || 'All Time',
          '',
          'normal',
          startIndex,
          patientId,
          sortInfo.sortBy,
          sortInfo.sortDirection
        )
      );
    }

    analytics.track(
      type === 'patientReset'
        ? 'Clicked Clear Filters To View All Time Patient Appointments'
        : 'Reset Filters',
      { email: user?.email }
    );
  };

  const showPatientResetLink =
    (filterValue?.doctorName?.length > 0 ||
      filterValue?.status ||
      filterValue?.startDate ||
      filterValue?.endDate) &&
    remainderPatientAppts !== 0 &&
    !isLoading;

  return (
    <>
      {isMobile ? (
        <AppointmentsMobilePage
          setData={setData}
          setSelectedAppointmentForAuraEncounter={
            setSelectedAppointmentForAuraEncounter
          }
          setDeleteText={setDeleteText}
          setQRCodeURL={setQRCodeURL}
          setStoreRecord={setStoreRecord}
          setShowConvertAppointmentToAuraModal={
            setShowConvertAppointmentToAuraModal
          }
          // doctor_nameSorter={doctor_nameSorter}
          onCellClick={onCellClick}
          hideDateTime={hideDateTime}
          // customDateTimeSorter={customDateTimeSorter}
          // condition_nameSorter={condition_nameSorter}
          filteredData={filteredData}
          setFilteredData={setFilteredData}
          filterValue={filterValue}
          showNoData={showNoData}
          isfilterOn={isfilterOn}
          isModalOpen={isModalOpen}
          handleCancel={handleCancel}
          setIsModalOpen={setIsModalOpen}
          startIndex={startIndex}
          showUpload={showUpload}
          setShowUpload={setShowUpload}
          currentPractitioner={currentPractitioner}
          showModal={showModal}
          deleteText={deleteText}
          QRCodeURL={QRCodeURL}
          editAppointmentOpen={editAppointmentOpen}
          apptsAPICall={apptsAPICall}
          isLoading={isLoading}
          getAppointmentStatusList={getAppointmentStatusList}
          filteredPatientNames={filteredPatientNames}
          filteredApptsTotal={filteredApptsTotal}
          apptsTotal={apptsTotal}
          beforepercent={beforepercent}
          itemsPerPage={itemsPerPage}
          handleShowMore={handleShowMore}
          data={data}
          showProfileLogo={showProfileLogo}
          setShowProfileLogo={setShowProfileLogo}
          // getReminderTime={getReminderTime}
          setEditAppointmentOpen={setEditAppointmentOpen}
          isDeleteOpen={isDeleteOpen}
          onChangeHandler={onChangeHandler}
          setIsDeleteOpen={setIsDeleteOpen}
          filterOptions={filterOptions}
          setIsFilterOn={setIsFilterOn}
          setShowNoData={setShowNoData}
          setFilteredPatientNames={setFilteredPatientNames}
          setFilterValue={setFilterValue}
          hasMultiplePractitionersWithSameName={
            hasMultiplePractitionersWithSameName
          }
          handleCustomSelect={handleCustomSelect}
          handleCancelModal={handleCancelModal}
          modalVisible={modalVisible}
          isCustomSelected={isCustomSelected}
          setModalVisible={setModalVisible}
          performFuzzySearch={performFuzzySearch}
          patientSearchKeyword={patientSearchKeyword}
          setPatientSearchKeyword={setPatientSearchKeyword}
          capitalizeFirstLetter={capitalizeFirstLetter}
          isQRCodeOpen={isQRCodeOpen}
          setIsQRCodeOpen={setIsQRCodeOpen}
          isnotificationHistoryOpen={isnotificationHistoryOpen}
          setIsnotificationHistoryOpen={setIsnotificationHistoryOpen}
          storeRecord={storeRecord}
          selectedPractitioners={selectedPractitioners}
          setSelectedPractitioners={setSelectedPractitioners}
          getAppointmentsByStatus={getAppointmentsByStatus}
          fetchCounterRef={fetchCounterRef}
          practitionerSearchKeyword={practitionerSearchKeyword}
          setpractitionerSearchKeyword={setpractitionerSearchKeyword}
          setUploadKey={setUploadKey}
          myAppointments={myAppointments}
          setMyAppointments={setMyAppointments}
          recentSearchList={recentSearchList}
          handleAddLabel={handleAddLabel}
          handleRecentSearchSelect={handleRecentSearchSelect}
          handleCurrentPractitionerNext7Days={
            handleCurrentPractitionerNext7Days
          }
          setDateValue={setDateValue}
          dateValue={dateValue}
          parseStoredNames={parseStoredNames}
          currentDate={currentDate}
          handleMonthChange={handleMonthChange}
          handleYearChange={handleYearChange}
          isModalVisible={isModalVisible}
          handleSaveView={handleSaveView}
          handleCloseView={handleCloseView}
          savePatientName={filterValue?.patientName}
          saveDate={filterValue?.datefilter}
          saveStatus={filterValue?.status}
          setRenameTitle={setRenameTitle}
          renameTitle={renameTitle}
          isMangeViewOpen={isMangeViewOpen}
          handleMangeViews={handleMangeViews}
          selectedTitle={selectedTitle}
          setSelectedTitle={setSelectedTitle}
          handleCloseManageView={handleCloseManageView}
          duplicateError={duplicateError}
          setDuplicateError={setDuplicateError}
          handleSaveAppDuplicate={handleSaveAppDuplicate}
          handleReset={handleReset}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          debouncedFetchPatientNames={debouncedFetchPatientNames}
          resetDateFilter={resetDateFilter}
          setResetDateFilter={setResetDateFilter}
          getAppointmentByFilter={getAppointmentByFilter}
          setSelectedFilters={setSelectedFilters}
          selectedFilters={selectedFilters}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          remainderPatientAppts={remainderPatientAppts}
          showPatientResetLink={showPatientResetLink}
        />
      ) : (
        <>
          <div className={`page-wrapper appointment-page`}>
            {!hideDashboardWidgetAccess && <AppointmentsCharts />}
            <Formik
              initialValues={{ gender: '' }} // Initial form values
              onSubmit={() => {}}
            >
              {({ handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <AppointmentTopFilterBar
                    setSelectedPractitioners={setSelectedPractitioners}
                    showUploadHistory={showUploadHistory}
                    showUpload={showUpload}
                    defaultAppointmentType={defaultAppointmentType}
                    handleCancel={handleCancel}
                    setIsModalOpen={setIsModalOpen}
                    isModalOpen={isModalOpen}
                    isMangeViewOpen={isMangeViewOpen}
                    setRenameTitle={setRenameTitle}
                    showConvertAppointmentToAuraModal={
                      showConvertAppointmentToAuraModal
                    }
                    hideConvertAppointmentToAuraModal={
                      hideConvertAppointmentToAuraModal
                    }
                    selectedAppointmentForAuraEncounter={
                      selectedAppointmentForAuraEncounter
                    }
                    handleSaveView={handleSaveView}
                    handleSaveAppDuplicate={handleSaveAppDuplicate}
                    duplicateError={duplicateError}
                    setDuplicateError={setDuplicateError}
                    parseStoredNames={parseStoredNames}
                    handleCloseView={handleCloseView}
                    isModalVisible={isModalVisible}
                    handleAddLabel={handleAddLabel}
                    handleRecentSearchSelect={handleRecentSearchSelect}
                    isCustomSelected={isCustomSelected}
                    setFilteredPatientNames={setFilteredPatientNames}
                    setShowProfileLogo={setShowProfileLogo}
                    selectedPractitioners={selectedPractitioners}
                    practitionersAndPatients={practitionersAndPatients}
                    filteredData={filteredData}
                    currentPractitioner={currentPractitioner}
                    isTenantMatching={isTenantMatching}
                    setpractitionerSearchKeyword={setpractitionerSearchKeyword}
                    onChangeHandler={onChangeHandler}
                    showProfileLogo={showProfileLogo}
                    hasMultiplePractitionersWithSameName={
                      hasMultiplePractitionersWithSameName
                    }
                    handleCloseManageView={handleCloseManageView}
                    appointmentsView={appointmentsView}
                    showDropdown={showDropdown}
                    showNoData={showNoData}
                    setPatientSearchKeyword={setPatientSearchKeyword}
                    currentDate={currentDate}
                    handleMonthChange={handleMonthChange}
                    handleYearChange={handleYearChange}
                    isfilterOn={isfilterOn}
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    resetDateFilter={resetDateFilter}
                    setResetDateFilter={setResetDateFilter}
                    selectDates={selectDates}
                    setSelectDates={setSelectDates}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    selectedFilters={selectedFilters}
                    renameTitle={renameTitle}
                    myAppointments={myAppointments}
                    selectedTitle={selectedTitle}
                    handleCurrentPractitionerNext7Days={
                      handleCurrentPractitionerNext7Days
                    }
                    handleMangeViews={handleMangeViews}
                    recentSearchList={recentSearchList}
                    showModal={showModal}
                    CSVUploadEnabledForCurrentUser={
                      CSVUploadEnabledForCurrentUser
                    }
                    setDefaultAppointmentType={setDefaultAppointmentType}
                    setShowUpload={setShowUpload}
                    setUploadKey={setUploadKey}
                    setShowUploadHistory={setShowUploadHistory}
                    filterOptions={filterOptions}
                    setIsFilterOn={setIsFilterOn}
                    setShowNoData={setShowNoData}
                    setShowDropdown={setShowDropdown}
                    handleReset={handleReset}
                    setStartIndex={setStartIndex}
                    setAppointmentsView={setAppointmentsView}
                    setMyAppointments={setMyAppointments}
                    setDateValue={setDateValue}
                    setSelectedFilters={setSelectedFilters}
                    filteredPatientNames={filteredPatientNames}
                    startIndex={startIndex}
                    performFuzzySearch={performFuzzySearch}
                    setSelectedTitle={setSelectedTitle}
                    isDefaultAppointmentView={isDefaultAppointmentView}
                    setDefaultAppointmentView={setDefaultAppointmentView}
                    setToStore={setToStore}
                  />

                  {deleteText && (
                    <div className="">
                      <DeleteAppointment
                        isOpen={isDeleteOpen}
                        onCancel={() => setIsDeleteOpen(false)}
                        setIsOpen={setIsDeleteOpen}
                        deleteText={deleteText}
                        startIndex={startIndex}
                        isfilterOn={isfilterOn}
                        filterValue={filterValue}
                      />
                    </div>
                  )}
                  {QRCodeURL && (
                    <div className="">
                      <ShowQRCode
                        isOpen={isQRCodeOpen}
                        onCancel={() => setIsQRCodeOpen(false)}
                        setIsOpen={setIsQRCodeOpen}
                        qrCodeValue={QRCodeURL}
                      />
                    </div>
                  )}
                  {editAppointmentOpen && (
                    <>
                      <EditAppointment
                        isOpen={editAppointmentOpen}
                        onCancel={() => setEditAppointmentOpen(false)}
                        setIsOpen={setEditAppointmentOpen}
                        startIndex={startIndex}
                        isfilterOn={isfilterOn}
                        filterValue={filterValue}
                      />
                    </>
                  )}

                  {isnotificationHistoryOpen && (
                    <>
                      <NotificationHistory
                        isOpen={isnotificationHistoryOpen}
                        setIsOpen={setIsnotificationHistoryOpen}
                        record={storeRecord}
                        filteredData={filteredData}
                      />
                    </>
                  )}

                  <div className="mt-5 ant-table rounded-xl shadow-xl w-full">
                    {appointmentsView === 'LIST' ? (
                      <>
                        {' '}
                        <Table
                          className={'appointment-page-table'}
                          rowClassName={(record) => {
                            let rowClass = '';
                            rowClass = 'hoverable-row';
                            if (record?.isExpired) {
                              rowClass = 'expired-row';
                            }
                            return rowClass.trim();
                          }}
                          data={filteredData}
                          columns={columns(
                            filteredData,
                            onCellClick,
                            practitionersAndPatients,
                            currentPractitioner,
                            hasVCAProcessedWorkflowAccess,
                            user,
                            setDeleteText,
                            setIsDeleteOpen,
                            setQRCodeURL,
                            setIsQRCodeOpen,
                            setEditAppointmentOpen,
                            setIsnotificationHistoryOpen,
                            setShowConvertAppointmentToAuraModal,
                            setSelectedAppointmentForAuraEncounter,
                            setFilteredData,
                            setData,
                            setStoreRecord,
                            width <= 1366,
                            sortInfo,
                            handleSort
                          )}
                          items={false}
                          loading={isLoading}
                          locale={{ emptyText: 'No Data' }}
                          additionalPatientInfo={
                            showPatientResetLink ? true : false
                          }
                          handleReset={handleReset}
                          remainderPatientAppts={remainderPatientAppts}
                        />
                        {filteredData?.length > 0 && (
                          <div className="flex flex-col justify-center items-center mt-4">
                            <p>
                              {`Showing ${filteredData?.length} of ${
                                filteredData?.length < itemsPerPage
                                  ? filteredData?.length
                                  : filteredApptsTotal
                                    ? filteredApptsTotal
                                    : filteredData?.length < itemsPerPage
                                      ? filteredData?.length
                                      : apptsTotal
                              } `}{' '}
                            </p>
                            <div className="flex justify-center items-center">
                              <Progress
                                percent={beforepercent * 100}
                                strokeColor="#00D090"
                                showInfo={false}
                                size="small"
                                style={{
                                  width: '200px',
                                  height: '2px',
                                }}
                              />
                            </div>
                          </div>
                        )}
                        <div className=" flex justify-center items-center p-4 mt-2">
                          {filteredData?.length > 0 &&
                            filteredData.length >= itemsPerPage &&
                            denominator >= startIndex && (
                              <button
                                onClick={handleShowMore}
                                disabled={isLoading ? true : false}
                                className="show-more-button bg-[#00D090] rounded-full hover:bg-[#059669] text-white font-semibold  mb-10 p-2 py-2 px-16"
                              >
                                {filteredData?.length !== startIndex &&
                                denominator > startIndex ? (
                                  <Bars
                                    height="25"
                                    width="25"
                                    color="#ffffff"
                                    ariaLabel="bars-loading"
                                    wrapperStyle={{}}
                                    wrapperClass=" justify-center"
                                    visible={true}
                                  />
                                ) : (
                                  ''
                                )}
                                {filteredData?.length !== startIndex &&
                                apptsTotal > startIndex
                                  ? ''
                                  : filteredData?.length !== startIndex
                                    ? ''
                                    : 'Show More'}
                              </button>
                            )}
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-4 full-calendar-wrapper">
                        <Calendar
                          className={'appointment-page-table'}
                          data={filteredData}
                          startIndex={startIndex}
                          isfilterOn={isfilterOn}
                          onChangeHandler={onChangeHandler}
                          setFilterValue={setFilterValue}
                          filterValue={filterValue}
                          setIsFilterOn={setIsFilterOn}
                          fetchCounterRef={fetchCounterRef}
                        />
                      </div>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </>
      )}
    </>
  );
};

export default AppointmentsPage;
